/**
 * Cognito sign-in for the workstation, and nothing for the phone.
 *
 * The hosted UI with PKCE rather than a password form of our own: we never
 * handle the password, and the redirect dance is handled by a library that has
 * already got the token refresh and the storage right.
 *
 * `useWorkstation()` is what screens actually use. It hands back the API
 * client already carrying a token, so no screen ever touches one.
 */
import { AuthProvider, useAuth } from 'react-oidc-context';
import type { ReactNode } from 'react';
import { WebStorageStateStore } from 'oidc-client-ts';
import { cognitoAuthority, config } from './config';
import { workstation } from './api';

const oidcConfig = {
  authority: cognitoAuthority,
  client_id: config.clientId,
  redirect_uri: window.location.origin,
  post_logout_redirect_uri: window.location.origin,
  response_type: 'code',
  scope: 'openid email',
  // sessionStorage, not localStorage: a workstation in a workshop is a shared
  // machine, and closing the browser should end the session rather than leave
  // the next person signed in as whoever printed last.
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  onSigninCallback: () => {
    // Strip ?code= and ?state= so a refresh does not replay the exchange.
    window.history.replaceState({}, '', window.location.pathname);
  },
};

export function Auth({ children }: { children: ReactNode }) {
  return <AuthProvider {...oidcConfig}>{children}</AuthProvider>;
}

/** Everything behind this is signed in, or is a sign-in prompt. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const auth = useAuth();

  if (auth.isLoading) return <p className="muted">Signing in…</p>;
  if (auth.error) {
    return (
      <div className="panel stop">
        <h2>Could not sign in</h2>
        <p>{auth.error.message}</p>
        <button onClick={() => void auth.signinRedirect()}>Try again</button>
      </div>
    );
  }
  if (!auth.isAuthenticated) {
    return (
      <div className="panel">
        <h2>Stitch Lane</h2>
        <p className="muted">Sign in to create jobs and review sheets.</p>
        <button className="primary" onClick={() => void auth.signinRedirect()}>
          Sign in
        </button>
      </div>
    );
  }
  return <>{children}</>;
}

/** The API client for the signed-in workstation. */
export function useWorkstation() {
  const auth = useAuth();
  const token = auth.user?.id_token;
  if (!token) throw new Error('useWorkstation used outside RequireAuth');
  return workstation(token);
}

export { useAuth };
